import { Button, ButtonProps, ModalProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import { JustRightHeightModal } from 'components/styled/scoped';
import useModal from 'hooks/useModal';
import { css } from '@emotion/css';
import { HolderOutlined, InfoCircleFilled } from '@ant-design/icons';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';
import { values, max } from 'lodash-es';
import { useMemoizedFn, useLatest } from 'ahooks';
import { produce } from 'immer';
import { todoSortCache } from 'services/cache';

export const getSortedInfo = (): Record<number, number> => {
  const sortCache = todoSortCache.getItem(sortKey);
  if (!sortCache) {
    return {};
  }
  try {
    return JSON.parse(sortCache);
  } catch (e) {
    return {};
  }
};

const SortableItem = SortableElement(({ value: policy }: { value: Todo.Policy }) => {
  const { getTypeLabel } = useTypeLabel();
  const name = policy.source === 0 ? getTypeLabel(policy.type) : policy.name;
  return (
    <div className="flex items-center mb-[12px] cursor-grab select-none">
      <HolderOutlined className="mr-[8px] text-[#999999]" />
      <span>{name}</span>
    </div>
  );
});
const SortableBox = SortableContainer(({ children }: { children: ReactNode }) => {
  return <div className="px-[24px] my-[16px]">{children}</div>;
});

interface SortTodoButtonProps extends ButtonProps, SortTodoProps {
  label?: string;
}
export const SortTodoButton = (props: SortTodoButtonProps) => {
  const { policies, onSorted, label, ...restProps } = props;
  const { t } = useTranslate();
  const { visible, show, hide } = useModal();
  return (
    <>
      <Button onClick={() => show()} {...restProps} type="link">
        {label || t('待办类型排序')}
      </Button>
      {visible && (
        <SortTodoModal onSorted={onSorted} policies={policies} visible={visible} onCancel={hide} />
      )}
    </>
  );
};

const sortKey = 'todo_policies_sort';

interface SortTodoProps {
  policies: Todo.Policy[];
  onSorted: () => void;
}
interface SortTodoModalProps extends ModalProps, SortTodoProps {}
export const SortTodoModal = (props: SortTodoModalProps) => {
  const { policies, onSorted, className, ...restProps } = props;
  const [sortInfo, setSortInfo] = useState(() => {
    const sortCache = getSortedInfo();
    let maxNum = max(values(sortCache)) || 0;
    return policies.reduce((acc, cur) => {
      if (!acc[cur.id]) {
        if (cur.id > maxNum) {
          acc[cur.id] = cur.id;
          maxNum = cur.id;
        } else {
          acc[cur.id] = maxNum + 1;
          maxNum += 1;
        }
      }
      return acc;
    }, sortCache as Record<number, number>);
  });
  const sortedPolicies = useMemo(() => {
    return policies.slice().sort((a, b) => sortInfo[a.id] - sortInfo[b.id]);
  }, [policies, sortInfo]);

  const { t } = useTranslate();
  const sortedPoliciesRef = useLatest(sortedPolicies);
  const onSortEnd = useMemoizedFn(({ oldIndex, newIndex }: any) => {
    if (newIndex === 0) {
      setSortInfo(
        produce((draft) => {
          draft[sortedPoliciesRef.current[oldIndex].id] =
            draft[sortedPoliciesRef.current[0].id] - 1;
        }),
      );
    }
    if (newIndex === sortedPoliciesRef.current.length - 1) {
      setSortInfo(
        produce((draft) => {
          draft[sortedPoliciesRef.current[oldIndex].id] =
            draft[sortedPoliciesRef.current[sortedPoliciesRef.current.length - 1].id] + 1;
        }),
      );
    }
    if (newIndex > 0 && newIndex < sortedPoliciesRef.current.length - 1) {
      setSortInfo(
        produce((draft) => {
          const prev =
            oldIndex > newIndex
              ? sortedPoliciesRef.current[newIndex - 1]
              : sortedPoliciesRef.current[newIndex + 1];
          const next = sortedPoliciesRef.current[newIndex];
          draft[sortedPoliciesRef.current[oldIndex].id] = (draft[prev.id] + draft[next.id]) / 2;
        }),
      );
    }
  });

  return (
    <JustRightHeightModal
      title={t('待办类型排序')}
      width={600}
      onOk={() => {
        todoSortCache.setItem(sortKey, JSON.stringify(sortInfo));
        restProps.onCancel?.(null!);
        onSorted?.();
      }}
      okText={t('phrase.保存')}
      className={css`
        .ant-modal-body {
          padding: 0 !important;
          margin: 0 !important;
        }
      `}
      {...restProps}
    >
      <div className="px-[16px] bg-[#f0f7ff] py-[5px] flex items-center">
        <InfoCircleFilled
          style={{
            color: '#3370ff',
          }}
        />
        <span className="leading-[20px] text-[#515359] ml-[8px]">{t('排序仅对当前账号生效')}</span>
      </div>
      <SortableBox
        onSortEnd={onSortEnd}
        helperClass={css`
          z-index: 99999;
        `}
      >
        {sortedPolicies?.map((each, index) => {
          return <SortableItem key={each.id} index={index} value={each} />;
        })}
      </SortableBox>
    </JustRightHeightModal>
  );
};
